import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { MutationOptions, QueryKey } from '../../../api/hooks/types';

export interface Option {
	name: string;
	value: string | boolean;
}

export interface SavedFilterOption {
	id: string;
	filter_type: string;
	options: Option[];
}

interface SavedFilterOptionsResponse {
	data: SavedFilterOption[];
}

interface CreateSavedFilterOptionParams {
	filter_type: string;
	options: Option[];
}

// GET - Fetch all saved filter options
export const useSavedFilterOptions = () => {
	const http = useHttpClient('v2');

	return useQuery([QueryKey.SavedFilterOptions], () => http.get<SavedFilterOptionsResponse>('user/saved_filter_options'), {
		select: (data) => data?.data.data,
	});
};

// POST - Create a new saved filter option
export const useCreateSavedFilterOption = (options?: MutationOptions<CreateSavedFilterOptionParams>) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(
		(params: CreateSavedFilterOptionParams) =>
			http.post<SavedFilterOptionsResponse>('user/saved_filter_options', {
				filter_option: params,
			}),
		{
			...options,
			onSuccess: (data, variables, context) => {
				queryClient.invalidateQueries([QueryKey.SavedFilterOptions]);
				options?.onSuccess?.(data, variables, context);
			},
		}
	);
};

// DELETE - Delete a saved filter option by id
export const useDeleteSavedFilterOption = (options?: MutationOptions<string>) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation((id: string) => http.delete(`user/saved_filter_options/${id}`), {
		...options,
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries([QueryKey.SavedFilterOptions]);
			options?.onSuccess?.(data, variables, context);
		},
	});
};
